import { useEffect } from 'react';
import { connect, MapDispatchToPropsParam } from 'react-redux';

import l from '../../../lang';

import { getBalances, putShouldAnimationsRun } from '../../../store/home/actions';

import OnboardingCard from './onboarding-card';
import FirstTimeCards from './first-time-cards';
import ReminderStepTwo from './reminder-step-two';
import ReminderStepTwoLimitReached from './reminder-step-two-limit-reached';
import ProductCards from './product-cards';
import Balances from './balances';

import TopDashboardSection from '../../common/top-dashboard-section';
import FraudMessage from '../fraud-message';

import './styles.scss';
import 'animate.css/animate.css';

interface HomeState {
  dashboard: {
    userMe: { name: string };
    merchantMe: {
      canOperate: boolean;
      kycLevel: { level: number; maxAmount: number };
      createdCheckouts: number;
      completedTransactions: number;
    };
  };
  home: {
    shouldAnimationsRun: boolean;
    balances: {
      data: {
        totalAccumulated: number;
      };
    };
  };
}

interface HomeDispatch {
  onGetBalances: () => {};
  putShouldAnimationsRun: (shouldRun: boolean) => {};
}

interface HomeStateProps {
  userMe: { name: string };
  balances: {
    data: {
      totalAccumulated: number;
    };
  };
  canOperate: boolean;
  createdCheckouts: number;
  completedTransactions: number;
  shouldAnimationsRun: boolean;
  kycLevel: { level: number; maxAmount: number };
}

interface HomeProps extends HomeDispatch, HomeStateProps {}

const Home = ({
  userMe,
  balances,
  onGetBalances,
  kycLevel: { level, maxAmount },
  canOperate,
  createdCheckouts,
  completedTransactions,
  shouldAnimationsRun,
  putShouldAnimationsRun,
}: HomeProps) => {
  useEffect(() => {
    onGetBalances();
  }, [onGetBalances]);

  // Running animations only once
  shouldAnimationsRun && setTimeout(() => putShouldAnimationsRun(false), 1000);

  const isFirstTime = createdCheckouts === 0 && completedTransactions === 0;

  // Limit is reached when the user has reached 90% of the total amount of transactions. (u$s 2.700  of u$s 3.000)
  const limitReached = maxAmount * 0.9 <= (balances?.data?.totalAccumulated || 0);

  return (
    <div className="home-component">
      <TopDashboardSection title={l('home.welcome.title', userMe?.name?.split(' ')[0] || '')} />
      <div className="home-component__fraud_message">
        <FraudMessage />
      </div>
      {!canOperate && <OnboardingCard shouldAnimationsRun={shouldAnimationsRun} />}

      {canOperate && isFirstTime && level < 4 && (
        <FirstTimeCards shouldAnimationsRun={shouldAnimationsRun} maxAmount={maxAmount} />
      )}

      {completedTransactions > 0 && level < 4 && !limitReached && <ReminderStepTwo />}

      {completedTransactions > 0 && level < 4 && limitReached && <ReminderStepTwoLimitReached />}

      {<ProductCards shouldAnimationsRun={shouldAnimationsRun && canOperate && !isFirstTime} />}

      {completedTransactions > 0 && <Balances balances={balances} />}
    </div>
  );
};

const mapStateToProps = (state: HomeState): HomeStateProps => ({
  userMe: state.dashboard.userMe,
  balances: state.home.balances,
  canOperate: state.dashboard.merchantMe?.canOperate,
  kycLevel: state.dashboard.merchantMe?.kycLevel,
  createdCheckouts: state.dashboard.merchantMe?.createdCheckouts,
  completedTransactions: state.dashboard.merchantMe?.completedTransactions,
  shouldAnimationsRun: state.home.shouldAnimationsRun,
});

const mapDispatchToProps: MapDispatchToPropsParam<HomeDispatch, HomeDispatch> = dispatch => ({
  onGetBalances: () => dispatch(getBalances()),
  putShouldAnimationsRun: (shouldRun: boolean) => dispatch(putShouldAnimationsRun(shouldRun)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Home);
